import React, { useEffect } from "react";
import { X } from "lucide-react";

interface CouponSelectSheetProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  /** 하단 CTA 클릭 (보통 적용 후 닫기) */
  onApply: () => void;
  /** 선택된 쿠폰이 없으면 true → CTA 비활성 */
  applyDisabled?: boolean;
  applyLabel?: string;
  /** 목록 최초 로딩 중 */
  loading?: boolean;
  /** 보유 쿠폰 0개 */
  isEmpty?: boolean;
  /** 다음 페이지 존재 */
  hasMore?: boolean;
  onLoadMore?: () => void;
  /** 쿠폰 목록 (각 모달의 <li> 아이템들) */
  children: React.ReactNode;
}

/** 로딩 중 실제 쿠폰 카드 모양을 흉내내는 스켈레톤 (progress-shimmer로 반짝임) */
const CouponSkeletonCard: React.FC = () => (
  <div className="relative flex overflow-hidden rounded-xl border border-gray-200 bg-white shadow-[0_1px_3px_rgba(0,0,0,0.04)]">
    {/* 티켓 노치 (실제 카드와 동일) */}
    <span className="absolute left-20 top-0 z-10 h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full bg-gray-100" />
    <span className="absolute left-20 bottom-0 z-10 h-3 w-3 -translate-x-1/2 translate-y-1/2 rounded-full bg-gray-100" />
    {/* 왼쪽 할인 블록 (실제 카드의 주황 틴트와 동일 위치) */}
    <div className="progress-shimmer flex w-20 shrink-0 flex-col items-center justify-center gap-1.5 bg-[#FFF0EA] py-4">
      <div className="h-5 w-10 rounded bg-[#f3d3c4]" />
      <div className="h-2.5 w-7 rounded bg-[#f3d3c4]" />
    </div>
    <div className="self-stretch border-l-2 border-dashed border-gray-200" />
    {/* 오른쪽 정보 + 우측 선택 원 자리 */}
    <div className="flex flex-1 items-center justify-between gap-2 px-4 py-3.5">
      <div className="flex flex-1 flex-col gap-2">
        <div className="progress-shimmer h-3.5 w-3/4 rounded bg-gray-200" />
        <div className="progress-shimmer h-3 w-2/5 rounded bg-gray-200" />
        <div className="progress-shimmer h-3 w-3/5 rounded bg-gray-100" />
      </div>
      <div className="h-5 w-5 shrink-0 rounded-full bg-gray-200" />
    </div>
  </div>
);

/**
 * 쿠폰 선택 바텀시트의 공용 골격(예약 쿠폰 모달 디자인 기준).
 * 티켓/쇼핑/예약 모달이 모두 이 시트를 사용해 동일한 UX를 가진다.
 */
const CouponSelectSheet: React.FC<CouponSelectSheetProps> = ({
  isOpen,
  onClose,
  title = "쿠폰 선택",
  onApply,
  applyDisabled = false,
  applyLabel = "쿠폰 적용하기",
  loading = false,
  isEmpty = false,
  hasMore = false,
  onLoadMore,
  children,
}) => {
  useEffect(() => {
    if (!isOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-end justify-center bg-black/45">
      <div className="flex max-h-[72vh] min-h-[44vh] w-full max-w-[430px] flex-col overflow-hidden rounded-t-[28px] bg-gray-100 shadow-[0_-18px_48px_rgba(0,0,0,0.22)]">
        <header className="shrink-0 border-b border-gray-100 bg-white px-5 pb-3 pt-4">
          <div className="relative flex h-11 items-center justify-center">
            <h2 className="m-0 text-lg font-bold text-gray-900" style={{ fontFamily: "Gmarket-B" }}>
              {title}
            </h2>
            <button
              type="button"
              className="absolute right-0 inline-flex h-10 w-10 items-center justify-center rounded-full text-gray-700 transition-colors hover:bg-gray-100"
              aria-label={`${title} 닫기`}
              onClick={onClose}
            >
              <X size={22} strokeWidth={2.5} />
            </button>
          </div>
        </header>

        <div className="flex flex-1 flex-col gap-3 overflow-y-auto px-5 py-4">
          <ul className="m-0 flex list-none flex-col gap-3 p-0">{children}</ul>

          {loading ? (
            <div className="flex flex-col gap-3" aria-busy="true" aria-label="쿠폰을 불러오는 중">
              <CouponSkeletonCard />
              <CouponSkeletonCard />
              <CouponSkeletonCard />
            </div>
          ) : null}

          {isEmpty ? (
            <div className="flex flex-col items-center justify-center gap-2 rounded-2xl bg-white px-6 py-12">
              <div className="flex h-24 w-24 items-center justify-center rounded-full bg-linear-to-br from-orange-50 to-[#FFE3D6]">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-11 w-11 text-[#e16036]"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={1.4}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z"
                  />
                </svg>
              </div>
              <p className="mt-2 text-balance text-base font-bold text-gray-900" style={{ fontFamily: "Gmarket-B" }}>
                사용 가능한 쿠폰이 없어요
              </p>
              <p className="text-pretty text-center text-xs leading-relaxed text-gray-700">
                다음에 더 좋은 혜택으로 찾아올게요!
              </p>
            </div>
          ) : null}

          {hasMore ? (
            <button
              type="button"
              className="h-12 w-full rounded-2xl border border-gray-200 bg-white text-sm font-bold text-gray-700 transition-[transform,border-color,color] duration-200 hover:border-[#e16036] hover:text-[#e16036] active:scale-[0.96]"
              onClick={onLoadMore}
            >
              쿠폰 더 불러오기
            </button>
          ) : null}

          <div className="h-24" />
        </div>

        <div className="shrink-0 border-t border-gray-200 bg-white/95 px-5 py-4 shadow-[0_-10px_30px_rgba(0,0,0,0.06)] backdrop-blur">
          <button
            type="button"
            className="h-[52px] w-full rounded-2xl bg-[#e16036] text-base font-bold text-white shadow-[0_8px_18px_rgba(225,96,54,0.22)] transition-[transform,opacity,background-color,box-shadow] duration-200 hover:opacity-90 active:scale-[0.96] disabled:scale-100 disabled:bg-gray-300 disabled:shadow-none"
            disabled={applyDisabled}
            onClick={onApply}
          >
            {applyLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CouponSelectSheet;
